<template>
  <div>
    <CModal
      :show.sync="showDualLoginModal"
      :closeOnBackdrop="false"
      centered
      size="sm"
    >
      <template #header>
        <h5 class="mb-0">Active Session Detected</h5>
      </template>
      <div class="p-2">
        You are already logged in on <strong>{{ existingDevice }}</strong>. Do you want to logout that session?
      </div>
      <template #footer>
        <CButton color="secondary" @click="onCancelDualLogin">No</CButton>
        <CButton color="primary" @click="onConfirmDualLogin">Yes</CButton>
      </template>
    </CModal>

    <section class="main min-vh-100 d-flex align-items-center">
      <div class="container">
        <div class="row align-items-center">
          <!-- Left Column: Login Form -->
          <div class="col-12 col-md-6">
            <div class="login-box">
              <h2 class="f-secondary text-primary-2 mb-5">Login</h2>
              <ValidationObserver ref="loginForm" v-slot="{ invalid }">
                <form @submit.prevent="submitForm">
                  <!-- Email Input -->
                  <div class="mb-3">
                    <label for="email" class="form-label">Email Address*</label>
                    <ValidationProvider
                      rules="required|email"
                      v-slot="{ errors }"
                      name="email"
                    >
                      <input
                        type="email"
                        v-model="user.email"
                        id="email"
                        autocomplete="off"
                        class="custom-input"
                      />
                      <span class="text-danger small">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </div>

                  <!-- Password Input -->
                  <div class="mb-3">
                    <label for="password" class="form-label">Password*</label>
                    <ValidationProvider
                      rules="required"
                      v-slot="{ errors }"
                      name="password"
                    >
                      <div class="password-field">
                        <input
                          :type="showPassword ? 'text' : 'password'"
                          v-model="user.password"
                          id="password"
                          autocomplete="new-password"
                          class="custom-input"
                        />
                        <i
                          :class="['fas', showPassword ? 'fa-eye-slash' : 'fa-eye', 'password-toggle-icon']"
                          @click="showPassword = !showPassword"
                        ></i>
                      </div>
                      <span class="text-danger small">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </div>
                  <div class="text-right">
                    <a
                      class="text-primary-2 cursor-pointer"
                      @click.prevent="redirectToForgot"
                    >
                      Forgot password?
                    </a>
                  </div>

                  <!-- Submit Button -->
                  <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="isLoading"
                  >
                    <span
                      v-if="isLoading"
                      class="spinner-border spinner-border-sm"
                    ></span>
                    <span v-else>Login</span>
                  </button>
                </form>
              </ValidationObserver>
            </div>
          </div>

          <!-- Right Column: Image -->
          <div class="col-md-6 d-none d-md-block m-0 p-0">
            <img
              src="../../../public/img/stm/landing-img.png"
              class="img-fluid"
              alt="Career Pathways"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { extend } from "vee-validate";
import { required, email } from "vee-validate/dist/rules";
import { mapActions, mapGetters } from "vuex";
import { LOGIN_URL, redirectToRegistrationUrl } from "../../helpers/helper";

// Extend validation rules
extend("required", { ...required, message: "This field is required" });
extend("email", { ...email, message: "Invalid email address" });

export default {
  data() {
    return {
      redirectToRegistrationUrl,
      user: {
        email: "",
        password: "",
      },
      isLoading: false,
      showPassword: false,
      showDualLoginModal: false,
      existingDevice: "",
    };
  },
  computed: {
    ...mapGetters(["isSuperCustomer", "getPathAcronymByOrgDetail"]),
  },
  methods: {
    ...mapActions(["login", "isEmailBelongsToAcronym", "showToast", "confirmDualLogin", "cancelDualLogin"]),
    async submitForm() {
      try {
        const isValid = await this.$refs.loginForm.validate();
        if (!isValid) {
          return;
        }

        this.isLoading = true;

        const data = {
          username: this.user?.email,
          password: this.user?.password,
        };

        const res = await this.login(data);
        if (res?.has_existing_session) {
          this.existingDevice = res.existing_device || "Another Device";
          this.showDualLoginModal = true;
        }
      } catch (error) {
        console.error("Login error:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async onConfirmDualLogin() {
      this.showDualLoginModal = false;
      await this.confirmDualLogin();
    },
    onCancelDualLogin() {
      this.showDualLoginModal = false;
      this.cancelDualLogin();
    },
    clearFields() {
      this.user = {
        email: "",
        password: "",
      };
    },
    redirectToForgot() {
      this.$router.push({
        path: "/forgot",
      });
    },
  },
  beforeRouteLeave(to, from, next) {
    this.clearFields();
    next();
  },
};
</script>

<style lang="scss" scoped>
.custom-input {
  border-radius: 10px;
  border: 2px solid rgb(100, 100, 100);
}
.password-field {
  position: relative;
  .custom-input {
    padding-right: 2.5rem;
  }
  .password-toggle-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #666;
  }
}
@import "../../../src/assets/scss/stm.scss";
</style>
